import logoImage from '../../../../assets/logo.jpeg'
import qrImage from '../../../../assets/qr.png'
import { supabase } from '../../../../lib/supabase'
import { type BillData, BillDataSchema } from '../interfaces/exportBill'
import { buildBillingPeriod } from './billingPeriod'

// ── Type for the raw JOIN row returned by Supabase ───────────────────────────

interface JoinedUtilityRow {
  id: string
  room_number: string
  contract_id: string | null
  elec_unit_price: number
  elec_prev_reading: number
  elec_curr_reading: number
  elec_units: number
  elec_total: number
  water_unit_price: number
  water_prev_reading: number
  water_curr_reading: number
  water_units: number
  water_total: number
  record_date: string
  payment_status: 'paid' | 'unpaid'
  rooms: {
    price_monthly: number
    maintenance_fee: number
    internet_fee: number
    furniture_fee: number
    other_fee: number
    other_fee_label: string
  } | null
  contracts: {
    tenant_id: string
    movein_date?: string | null
    profiles: {
      first_name: string | null
      last_name: string | null
      phone: string | null
    } | null
  } | null
}

// ── Helper: get first day of next month ────────────────────────────────────

function nextMonthStart(yearMonth: string): string {
  const [year, month] = yearMonth.split('-').map(Number)
  const nextMonth = month === 12 ? 1 : month + 1
  const nextYear = month === 12 ? year + 1 : year
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`
}

// ── Transform a raw JOIN row to BillData (+ Zod validation) ─────────────────

function transformRow(row: JoinedUtilityRow): BillData {
  if (!row.rooms) throw new Error(`Room data missing for record ${row.id}`)
  const period = buildBillingPeriod(
    row.record_date,
    row.contracts?.movein_date ?? null,
  )

  const raw = {
    recordId: row.id,
    roomNumber: row.room_number,
    contractId: row.contract_id?.trim() ? row.contract_id : null,

    ...period,

    rent: row.rooms.price_monthly,
    maintenanceFee: row.rooms.maintenance_fee,
    internet: row.rooms.internet_fee,
    furniture: row.rooms.furniture_fee,
    fee: row.rooms.other_fee,
    otherFeeLabel: row.rooms.other_fee_label,

    waterPrev: row.water_prev_reading,
    waterNow: row.water_curr_reading,
    waterUnits: row.water_units,
    waterUnitPrice: row.water_unit_price,
    waterTotal: row.water_total,

    elecPrev: row.elec_prev_reading,
    elecNow: row.elec_curr_reading,
    elecUnits: row.elec_units,
    elecUnitPrice: row.elec_unit_price,
    electricTotal: row.elec_total,

    total:
      row.rooms.price_monthly +
      row.rooms.maintenance_fee +
      row.rooms.internet_fee +
      row.rooms.furniture_fee +
      row.rooms.other_fee +
      row.water_total +
      row.elec_total,

    paymentStatus: row.payment_status,

    tenantFirstName: row.contracts?.profiles?.first_name ?? null,
    tenantLastName: row.contracts?.profiles?.last_name ?? null,
    tenantPhone: row.contracts?.profiles?.phone ?? null,

    qrUrl: qrImage,
    logoUrl: logoImage,
  }

  return BillDataSchema.parse(raw)
}

// ── Type for the raw monthly_snapshot_rows DB row ────────────────────────────

interface SnapshotRow {
  id: string
  snapshot_id: string
  room_number: string
  tenant_name: string | null
  contract_id: string | null
  price_monthly: number
  elec_unit_price: number
  elec_prev_reading: number
  elec_curr_reading: number
  elec_units: number
  elec_total: number
  water_unit_price: number
  water_prev_reading: number
  water_curr_reading: number
  water_units: number
  water_total: number
  internet_fee: number
  furniture_fee: number
  maintenance_fee: number
  other_fee: number
  other_fee_label: string
  grand_total: number
  payment_status: 'paid' | 'unpaid'
  record_date: string
}

// ── Transform a snapshot row to BillData ─────────────────────────────────────

function transformSnapshotRow(
  row: SnapshotRow,
  moveInDate?: string | null,
): BillData {
  const period = buildBillingPeriod(row.record_date, moveInDate)
  const nameParts = (row.tenant_name ?? '').trim().split(/\s+/)
  const tenantFirstName = nameParts[0] ?? null
  const tenantLastName =
    nameParts.length > 1 ? nameParts.slice(1).join(' ') : null

  const raw = {
    recordId: row.id,
    roomNumber: row.room_number,
    contractId: row.contract_id,

    ...period,

    rent: row.price_monthly,
    maintenanceFee: row.maintenance_fee,
    internet: row.internet_fee,
    furniture: row.furniture_fee,
    fee: row.other_fee,
    otherFeeLabel: row.other_fee_label,

    waterPrev: row.water_prev_reading,
    waterNow: row.water_curr_reading,
    waterUnits: row.water_units,
    waterUnitPrice: row.water_unit_price,
    waterTotal: row.water_total,

    elecPrev: row.elec_prev_reading,
    elecNow: row.elec_curr_reading,
    elecUnits: row.elec_units,
    elecUnitPrice: row.elec_unit_price,
    electricTotal: row.elec_total,

    total: row.grand_total,

    paymentStatus: row.payment_status,

    tenantFirstName,
    tenantLastName,
    tenantPhone: null,

    qrUrl: qrImage,
    logoUrl: logoImage,
  }

  return BillDataSchema.parse(raw)
}

// ── Internal: resolve the latest snapshot id for a given month ───────────────

async function fetchLatestSnapshotId(
  yearMonth: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from('monthly_snapshots')
    .select('id')
    .eq('snapshot_month', yearMonth)
    .order('snapshotted_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw new Error(error.message)
  return data?.id ?? null
}

async function fetchMoveInDatesByContractIds(
  contractIds: string[],
): Promise<Map<string, string | null>> {
  const uniqueContractIds = [...new Set(contractIds)].filter(Boolean)
  if (uniqueContractIds.length === 0) return new Map()

  const { data, error } = await supabase
    .from('contracts')
    .select('id, movein_date')
    .in('id', uniqueContractIds)

  if (error) throw new Error(error.message)
  return new Map(
    (data ?? []).map((row) => [row.id, row.movein_date ?? null] as const),
  )
}

async function transformSnapshotRows(rows: SnapshotRow[]): Promise<BillData[]> {
  const moveInDates = await fetchMoveInDatesByContractIds(
    rows.flatMap((row) => (row.contract_id ? [row.contract_id] : [])),
  )
  return rows.map((row) =>
    transformSnapshotRow(
      row,
      row.contract_id ? (moveInDates.get(row.contract_id) ?? null) : null,
    ),
  )
}

// ── Query helpers ─────────────────────────────────────────────────────────────

const SELECT_FIELDS = `
  id,
  room_number,
  contract_id,
  elec_unit_price,
  elec_prev_reading,
  elec_curr_reading,
  elec_units,
  elec_total,
  water_unit_price,
  water_prev_reading,
  water_curr_reading,
  water_units,
  water_total,
  record_date,
  payment_status,
  rooms!left(price_monthly, maintenance_fee, internet_fee, furniture_fee, other_fee, other_fee_label),
  contracts!left(tenant_id, movein_date, profiles!left(first_name, last_name, phone))
`

// ── Public API ────────────────────────────────────────────────────────────────

export async function fetchBillByRoomAndMonth(
  roomNumber: string,
  yearMonth: string,
): Promise<BillData> {
  const rangeStart = `${yearMonth}-01`
  const rangeEnd = nextMonthStart(yearMonth)

  const { data, error } = await supabase
    .from('utility_records')
    .select(SELECT_FIELDS)
    .eq('room_number', roomNumber)
    .gte('record_date', rangeStart)
    .lt('record_date', rangeEnd)
    .limit(1)
    .maybeSingle()

  if (error) throw new Error(error.message)
  if (!data)
    throw new Error(
      `No utility record found for room ${roomNumber} in ${yearMonth}.`,
    )
  return transformRow(data as unknown as JoinedUtilityRow)
}

export async function fetchAllBillsByMonth(
  yearMonth: string,
): Promise<BillData[]> {
  const rangeStart = `${yearMonth}-01`
  const rangeEnd = nextMonthStart(yearMonth)

  const { data, error } = await supabase
    .from('utility_records')
    .select(SELECT_FIELDS)
    .gte('record_date', rangeStart)
    .lt('record_date', rangeEnd)
    .order('room_number')

  if (error) throw new Error(error.message)
  return (data as unknown as JoinedUtilityRow[]).map(transformRow)
}

// ── Snapshot-sourced variants ─────────────────────────────────────────────────

/** Returns the snapshotted_at timestamp of the latest snapshot for a month, or null if none. */
export async function fetchLatestSnapshotMeta(
  yearMonth: string,
): Promise<{ snapshottedAt: string } | null> {
  const { data, error } = await supabase
    .from('monthly_snapshots')
    .select('snapshotted_at')
    .eq('snapshot_month', yearMonth)
    .order('snapshotted_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw new Error(error.message)
  return data ? { snapshottedAt: data.snapshotted_at } : null
}

/** Returns all bills for a month from the latest snapshot, or [] if none. */
export async function fetchSnapshotBillsByMonth(
  yearMonth: string,
): Promise<BillData[]> {
  const snapshotId = await fetchLatestSnapshotId(yearMonth)
  if (!snapshotId) return []

  const { data, error } = await supabase
    .from('monthly_snapshot_rows')
    .select('*')
    .eq('snapshot_id', snapshotId)
    .order('room_number')

  if (error) throw new Error(error.message)
  return transformSnapshotRows(data as unknown as SnapshotRow[])
}

/** Returns a single bill for a room+month from the latest snapshot, or null if none. */
export async function fetchSnapshotBillByRoomAndMonth(
  roomNumber: string,
  yearMonth: string,
): Promise<BillData | null> {
  const snapshotId = await fetchLatestSnapshotId(yearMonth)
  if (!snapshotId) return null

  const { data, error } = await supabase
    .from('monthly_snapshot_rows')
    .select('*')
    .eq('snapshot_id', snapshotId)
    .eq('room_number', roomNumber)
    .limit(1)
    .maybeSingle()

  if (error) throw new Error(error.message)
  if (!data) return null
  const [bill] = await transformSnapshotRows([data as unknown as SnapshotRow])
  return bill ?? null
}
